import React, { useEffect, useState } from "react";
import { AlertCircle } from "lucide-react";
import Shimmer from "../Common/Shimmer";

/**
 * SheetTable — a spreadsheet (XLS / XLSX / CSV) rendered in the browser.
 *
 * Used for view-only spreadsheets, which cannot go through Office Online: that
 * viewer fetches the file from Microsoft's servers (so it needs a public URL)
 * and offers its own download. Here the file is parsed from the in-memory copy
 * ContentViewer already holds, and cells render as text — no formulas run, and
 * no markup from a cell reaches the page.
 *
 * Large sheets are capped so a 50,000-row export cannot freeze the tab.
 */
const MAX_ROWS = 1000;
const MAX_COLS = 60;

const columnName = (i) => {
  let name = "";
  for (let n = i + 1; n > 0; n = Math.floor((n - 1) / 26)) {
    name = String.fromCharCode(65 + ((n - 1) % 26)) + name;
  }
  return name;
};

export default function SheetTable({ url }) {
  const [sheets, setSheets] = useState(null);
  const [active, setActive] = useState(0);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setSheets(null);
    setError(false);
    setActive(0);
    (async () => {
      try {
        const [mod, resp] = await Promise.all([import("xlsx"), fetch(url)]);
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const XLSX = mod.read ? mod : mod.default;
        const book = XLSX.read(await resp.arrayBuffer(), { type: "array", cellDates: true });
        const parsed = book.SheetNames.map((name) => ({
          name,
          rows: XLSX.utils.sheet_to_json(book.Sheets[name], { header: 1, raw: false, defval: "", blankrows: false }),
        }));
        if (!cancelled) setSheets(parsed);
      } catch {
        if (!cancelled) setError(true);
      }
    })();
    return () => { cancelled = true; };
  }, [url]);

  if (error) {
    return (
      <div className="tw-flex tw-flex-col tw-items-center tw-gap-2 tw-p-8 tw-text-sm tw-text-content-muted">
        <AlertCircle size={24} aria-hidden="true" />
        <p className="tw-m-0">This spreadsheet could not be displayed.</p>
      </div>
    );
  }
  if (!sheets) return <div className="tw-p-4"><Shimmer height={36} count={6} /></div>;

  const sheet = sheets[active] || { rows: [] };
  const rows = sheet.rows.slice(0, MAX_ROWS);
  const colCount = Math.min(MAX_COLS, rows.reduce((max, r) => Math.max(max, r.length), 0));
  const truncated = sheet.rows.length > MAX_ROWS || rows.some((r) => r.length > MAX_COLS);

  return (
    <div className="tw-flex tw-min-h-0 tw-flex-col">
      {sheets.length > 1 && (
        <div className="tw-flex tw-gap-1 tw-overflow-x-auto tw-border-0 tw-border-b tw-border-solid tw-border-line tw-px-3 tw-py-2" role="group" aria-label="Sheets">
          {sheets.map((s, i) => (
            <button
              key={s.name}
              type="button"
              aria-pressed={i === active}
              onClick={() => setActive(i)}
              className={
                i === active
                  ? "tw-whitespace-nowrap tw-rounded tw-border-0 tw-bg-primary-soft tw-px-3 tw-py-1 tw-text-xs tw-font-semibold tw-text-primary-soft-fg"
                  : "tw-whitespace-nowrap tw-rounded tw-border-0 tw-bg-transparent tw-px-3 tw-py-1 tw-text-xs tw-text-content-muted hover:tw-bg-surface-hover"
              }
            >
              {s.name}
            </button>
          ))}
        </div>
      )}
      <div className="tw-max-h-[70vh] tw-overflow-auto">
        {rows.length === 0 ? (
          <p className="tw-m-0 tw-p-6 tw-text-center tw-text-sm tw-text-content-muted">This sheet is empty.</p>
        ) : (
          <table className="tw-w-max tw-min-w-full tw-border-collapse tw-text-xs tw-text-content">
            <thead>
              <tr>
                <th className="tw-sticky tw-top-0 tw-bg-surface-sunken tw-px-2 tw-py-1 tw-text-content-subtle" aria-label="Row" />
                {Array.from({ length: colCount }, (_, c) => (
                  <th key={c} scope="col" className="tw-sticky tw-top-0 tw-bg-surface-sunken tw-px-2 tw-py-1 tw-font-semibold tw-text-content-subtle">
                    {columnName(c)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, r) => (
                <tr key={r}>
                  <th scope="row" className="tw-bg-surface-sunken tw-px-2 tw-py-1 tw-text-right tw-font-normal tw-text-content-subtle">
                    {r + 1}
                  </th>
                  {Array.from({ length: colCount }, (_, c) => (
                    <td key={c} className="tw-max-w-[20rem] tw-truncate tw-border tw-border-solid tw-border-line-soft tw-px-2 tw-py-1">
                      {String(row[c] ?? "")}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {truncated && (
        <p className="tw-m-0 tw-px-3 tw-py-2 tw-text-xs tw-text-content-subtle">
          Showing the first {MAX_ROWS.toLocaleString("en-IN")} rows and {MAX_COLS} columns.
        </p>
      )}
    </div>
  );
}
